import { Link } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import PageHero from '@/components/feature/PageHero';
import { introHighlights, partners, stats } from '@/mocks/home';

const HERO_IMAGE =
  'https://readdy.ai/api/search-image?query=Artistic%20scene%20blending%20traditional%20Chinese%20medicine%20aesthetics%20with%20modern%20digital%20technology%2C%20ink%20brush%20strokes%20merging%20into%20luminous%20data%20network%20lines%2C%20warm%20crimson%20and%20jade%20green%20accents%2C%20cinematic%20soft%20lighting%20with%20dark%20lower%20area%20for%20text%20overlay&width=1920&height=560&seq=about-hero&orientation=landscape';

const missions = [
  {
    icon: 'ri-focus-3-line',
    title: '平台使命',
    desc: '以数智化转型与中西医协同为牵引，构建覆盖学习、临床、科研、协作、转化、服务全链条的整合型健康服务生态，让优质中医药资源惠及更多人群。',
  },
  {
    icon: 'ri-eye-line',
    title: '平台愿景',
    desc: '成为国内领先、面向政府、医院、基层、医师、患者与产业合作方的全国性中西医结合智慧生态服务门户，推动中医药在传承中创新、在创新中发展。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '价值理念',
    desc: '坚持标准筑基、数智赋能、生态融合、服务民生，以开放共享的姿态连接各方资源，让辨证论治的东方智慧与循证医学的科学方法协同发力。',
  },
];

export default function AboutPage() {
  return (
    <PageShell>
      <PageHero
        image={HERO_IMAGE}
        eyebrow="平台介绍"
        title="中西医结合智慧生态平台"
        description="围绕“以数智化转型与中西医协同为牵引”的战略导向，建设面向政府、医院、基层机构、医师、患者及产业合作方的全国性智慧生态服务门户。"
        breadcrumb={[{ label: '首页', to: '/' }, { label: '平台介绍' }]}
      />

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="w-full h-[320px] md:h-[420px] rounded-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Artistic%20scene%20blending%20traditional%20Chinese%20medicine%20and%20digital%20health%20technology%2C%20dried%20herbs%20and%20a%20ceramic%20mortar%20beside%20a%20glowing%20semi%20transparent%20data%20interface%2C%20warm%20ivory%20and%20crimson%20tones%2C%20soft%20studio%20lighting%2C%20elegant%20minimal%20editorial%20photography%2C%20high%20detail%20harmonious%20composition&width=900&height=1100&seq=about-intro&orientation=portrait"
              alt="中西医结合智慧生态平台"
              title="中西医结合智慧生态平台 - 数智化与中西医协同"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-px bg-primary-500"></span>
              <span className="text-sm font-medium tracking-wide text-primary-600">关于元合 VITA</span>
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-snug text-balance mb-5">
              汇聚名医名科名院，构建整合型健康服务生态
            </h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-800 leading-relaxed">
              <p>
                中西医结合智慧生态平台以全球领先的数据基座及算法模型为底层技术支撑，
                践行“标准筑基、数智赋能、生态融合、服务民生”理念，汇聚名医名科名院、科研院校、产业伙伴等优质资源，
                构建覆盖学习-临床-科研-协作-转化-应用-推广-服务-数据-治理-开发-入表的完整服务链条。
              </p>
              <p>
                面向政府、医院、基层、医师、患者及产业合作方，提供一站式整合型健康服务，
                推动中医药事业与产业高质量发展，让名医的临床智慧可沉淀、可传承、可服务于更多人。
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/courses"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                进入课程中心
              </Link>
              <Link
                to="/"
                state={{ scrollTo: 'contact' }}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-primary-200 text-primary-600 hover:bg-primary-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-customer-service-2-line"></i>
                生态合作咨询
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {missions.map((item) => (
              <div key={item.title} className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200">
                <span className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 mb-5">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 text-balance">六大核心能力</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              从数智化转型到全周期服务，从国医传承到真实世界研究，构建平台差异化的核心能力体系。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {introHighlights.map((item) => (
              <div
                key={item.id}
                className="group flex gap-4 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200 hover:border-accent-300 transition-colors"
              >
                <span className="shrink-0 w-12 h-12 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-16 bg-secondary-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((item) => (
              <div key={item.id} className="text-center">
                <span className="w-14 h-14 inline-flex items-center justify-center rounded-full bg-background-50 text-primary-500 mb-4">
                  <i className={`${item.icon} text-2xl`}></i>
                </span>
                <div className="font-heading font-bold text-3xl md:text-4xl text-foreground-900">
                  {item.value}
                  <span className="text-secondary-700">{item.suffix}</span>
                  <span className="ml-1 text-base text-foreground-600">{item.unit}</span>
                </div>
                <p className="mt-2 text-sm text-foreground-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 text-balance">生态合作伙伴</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              携手行业协会、科研院校与产业伙伴，共同推动中西医结合生态的开放共建。
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {partners.map((partner) => (
              <div
                key={partner.id}
                className="flex flex-col items-center justify-center gap-3 p-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  title={`${partner.short} - 生态合作伙伴`}
                  className="h-12 w-auto object-contain"
                />
                <span className="text-xs text-foreground-500 text-center leading-relaxed">{partner.short}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-16 bg-primary-700">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 text-balance">
              携手共建中西医结合智慧生态
            </h2>
            <p className="mt-3 text-sm md:text-base text-background-100/90 max-w-xl leading-relaxed">
              无论您是医疗机构、科研院校还是产业伙伴，欢迎加入我们，共同服务健康中国。
            </p>
          </div>
          <Link
            to="/"
            state={{ scrollTo: 'contact' }}
            className="inline-flex items-center gap-2 bg-background-50 hover:bg-background-100 text-primary-700 text-sm font-medium px-7 py-3.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-team-line"></i>
            立即合作洽谈
          </Link>
        </div>
      </section>
    </PageShell>
  );
}